import { useDeferredValue, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { ComponentErrorBoundary } from '@/components/shared/ComponentErrorBoundary';
import { AppSelect } from '@/components/shared/AppSelect';
import { Badge } from '@/components/shared/Badge';
import { DataStatePanel } from '@/components/shared/DataStatePanel';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/shared/PageHeader';
import { Panel } from '@/components/shared/Panel';
import { PlayerNameButton } from '@/components/shared/PlayerNameButton';
import { StatCard } from '@/components/shared/StatCard';
import { usePlayerSheet } from '@/app/contexts/PlayerSheetContext';
import { getPlayerAttendanceStats } from '@/features/attendance/selectors';
import { useGuildData } from '@/hooks/useGuildData';
import { formatDateTime } from '@/utils/date';
import { formatAppRoleLabel, getPrimaryRole } from '@/utils/roles';

type StatsSort = 'attendance' | 'latest' | 'level';

function getUsageWidth(count: number, maxCount: number): string {
  if (maxCount <= 0) {
    return '0%';
  }

  return `${Math.max(12, Math.round((count / maxCount) * 100))}%`;
}

function normalizeSearchText(value: unknown): string {
  return typeof value === 'string' ? value.toLowerCase() : '';
}

function getComparableTime(value: string | undefined): number {
  if (!value) {
    return Number.NEGATIVE_INFINITY;
  }

  const nextTime = new Date(value).getTime();
  return Number.isFinite(nextTime) ? nextTime : Number.NEGATIVE_INFINITY;
}

function PlayerStatsWorkspace(): JSX.Element {
  const { state, isLoading, error, refreshData } = useGuildData();
  const { openPlayerSheet } = usePlayerSheet();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<StatsSort>('attendance');
  const stats = useMemo(() => (isLoading ? [] : getPlayerAttendanceStats(state)), [isLoading, state]);
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = deferredQuery.trim().toLowerCase();

  const filteredStats = useMemo(
    () =>
      [...stats]
        .filter((player) => {
          if (!normalizedQuery) {
            return true;
          }

          return (
            normalizeSearchText(player.inGameName).includes(normalizedQuery) ||
            normalizeSearchText(player.displayName).includes(normalizedQuery) ||
            player.weaponUsage.some((usage) => normalizeSearchText(usage.weaponName).includes(normalizedQuery)) ||
            player.roleUsage.some((usage) => normalizeSearchText(usage.roleCategory).includes(normalizedQuery))
          );
        })
        .sort((left, right) => {
          if (sort === 'latest') {
            return getComparableTime(right.lastAttendedAt) - getComparableTime(left.lastAttendedAt);
          }

          if (sort === 'level') {
            if (right.progressionLevel !== left.progressionLevel) {
              return right.progressionLevel - left.progressionLevel;
            }
          }

          if (right.totalCountedMasses !== left.totalCountedMasses) {
            return right.totalCountedMasses - left.totalCountedMasses;
          }

          return left.inGameName.localeCompare(right.inGameName);
        }),
    [normalizedQuery, sort, stats],
  );

  const totalCountedMasses = useMemo(
    () => stats.reduce((sum, player) => sum + player.totalCountedMasses, 0),
    [stats],
  );
  const totalRecordedSignups = useMemo(
    () => stats.reduce((sum, player) => sum + player.totalSignupRecords, 0),
    [stats],
  );
  const highestLevel = useMemo(
    () => stats.reduce((max, player) => Math.max(max, player.progressionLevel), 1),
    [stats],
  );
  const mostActivePlayer = stats[0];
  const recentlySeenCount = useMemo(
    () =>
      stats.filter((player) => {
        const lastAttendedTime = getComparableTime(player.lastAttendedAt);

        if (!Number.isFinite(lastAttendedTime)) {
          return false;
        }

        return Date.now() - lastAttendedTime <= 1000 * 60 * 60 * 24 * 14;
      }).length,
    [stats],
  );

  return (
    <AppShell>
      <PageHeader title="Player Stats" description="Attendance, levels, roles, and loadouts for approved guild members." />

      {isLoading ? (
        <DataStatePanel title="Loading player stats" description="Refreshing attendance records, roles, and usage history." />
      ) : error ? (
        <DataStatePanel title="Unable to load player stats" description={error} actionLabel="Retry" onAction={() => void refreshData()} />
      ) : (
        <>
          <div className="stats-grid">
            <StatCard label="Tracked Players" value={stats.length} hint="Approved members" />
            <StatCard label="Counted Masses" value={totalCountedMasses} hint="Real attendance" />
            <StatCard label="Recorded Signups" value={totalRecordedSignups} hint="Tracked signup history" />
            <StatCard label="Highest Level" value={highestLevel} hint="Attendance progression" />
            <StatCard label="Active In 14 Days" value={recentlySeenCount} hint="Recently counted" />
          </div>

          <Panel title="Roster Progression" description="Search, sort, and inspect players by attendance, role, level, and loadout.">
            <div className="form-grid form-grid--two">
              <label className="field">
                <span className="field__label">Search Players</span>
                <input
                  className="field__input"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by in-game name, Discord name, role, or weapon"
                />
              </label>
              <div className="field">
                <span className="field__label">Sort</span>
                <AppSelect
                  ariaLabel="Attendance sort"
                  value={sort}
                  onChange={(value) => setSort(value as StatsSort)}
                  options={[
                    { value: 'attendance', label: 'Most Attendance', description: 'Highest counted mass total first' },
                    { value: 'level', label: 'Highest Level', description: 'Attendance progression level first' },
                    { value: 'latest', label: 'Latest Seen', description: 'Most recent counted mass first' },
                  ]}
                />
              </div>
            </div>

            {mostActivePlayer ? (
              <div
                className="player-stats__leader player-stats__leader--interactive"
                role="button"
                tabIndex={0}
                onClick={() => openPlayerSheet(mostActivePlayer.profileId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openPlayerSheet(mostActivePlayer.profileId);
                  }
                }}
              >
                <div>
                  <p className="guild-chat__eyebrow">MOST ACTIVE</p>
                  <PlayerNameButton
                    profileId={mostActivePlayer.profileId}
                    primaryLabel={mostActivePlayer.inGameName}
                    secondaryLabel={mostActivePlayer.displayName}
                    className="player-stats__leader-name"
                  />
                </div>
                <div className="slot-card__badges">
                  <Badge tone="default">{mostActivePlayer.totalSignupRecords} recorded signups</Badge>
                  <Badge tone="success">{mostActivePlayer.totalCountedMasses} counted masses</Badge>
                  <Badge tone="info">Level {mostActivePlayer.progressionLevel}</Badge>
                  <Badge tone="default">{mostActivePlayer.progressionTier}</Badge>
                  <Badge tone="warning">{formatAppRoleLabel(getPrimaryRole(mostActivePlayer.roles))}</Badge>
                </div>
              </div>
            ) : null}

            {filteredStats.length > 0 ? (
              <div className="attendance-grid">
                {filteredStats.map((player) => {
                  const maxWeaponUsage = Math.max(...player.weaponUsage.map((usage) => usage.usageCount), 0);
                  const maxRoleUsage = Math.max(...player.roleUsage.map((usage) => usage.usageCount), 0);

                  return (
                    <article
                      key={player.profileId}
                      className="attendance-card attendance-card--interactive"
                      role="button"
                      tabIndex={0}
                      onClick={() => openPlayerSheet(player.profileId)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openPlayerSheet(player.profileId);
                        }
                      }}
                    >
                      <div className="attendance-card__header">
                        <PlayerNameButton
                          profileId={player.profileId}
                          primaryLabel={player.inGameName}
                          secondaryLabel={player.displayName}
                          className="attendance-card__player"
                        />
                        <div className="slot-card__badges">
                          <Badge tone="default">{player.totalSignupRecords} recorded signups</Badge>
                          <Badge tone="success">{player.totalCountedMasses} counted masses</Badge>
                          <Badge tone="info">Level {player.progressionLevel}</Badge>
                          <Badge tone="default">{player.progressionTier}</Badge>
                          <Badge tone="warning">{formatAppRoleLabel(getPrimaryRole(player.roles))}</Badge>
                          {player.badges.slice(0, 2).map((badge) => (
                            <Badge key={badge.badgeId} tone="warning">
                              {badge.displayName}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="attendance-card__meta">
                        <span>{player.lastAttendedMassTitle ? `Last mass: ${player.lastAttendedMassTitle}` : 'No completed mass yet'}</span>
                        <span>{player.lastAttendedAt ? formatDateTime(player.lastAttendedAt) : 'No attendance recorded yet'}</span>
                      </div>

                      <div className="attendance-card__breakdown">
                        <div className="attendance-card__section">
                          <span className="profile-summary__label">Weapon Usage</span>
                          <div className="usage-list">
                            {player.weaponUsage.length > 0 ? (
                              player.weaponUsage.slice(0, 4).map((weapon) => (
                                <div
                                  key={weapon.weaponName}
                                  className="usage-row"
                                  style={{ ['--usage-width' as string]: getUsageWidth(weapon.usageCount, maxWeaponUsage) }}
                                >
                                  <div className="usage-row__labels">
                                    <strong>{weapon.weaponName}</strong>
                                    <span>x{weapon.usageCount}</span>
                                  </div>
                                  <div className="usage-row__track">
                                    <span className="usage-row__fill" />
                                  </div>
                                </div>
                              ))
                            ) : (
                              <Badge tone="default">No weapon history yet</Badge>
                            )}
                          </div>
                        </div>

                        <div className="attendance-card__section">
                          <span className="profile-summary__label">Role Usage</span>
                          <div className="usage-list">
                            {player.roleUsage.length > 0 ? (
                              player.roleUsage.slice(0, 4).map((role) => (
                                <div
                                  key={role.roleCategory}
                                  className="usage-row"
                                  style={{ ['--usage-width' as string]: getUsageWidth(role.usageCount, maxRoleUsage) }}
                                >
                                  <div className="usage-row__labels">
                                    <strong>{role.roleCategory}</strong>
                                    <span>x{role.usageCount}</span>
                                  </div>
                                  <div className="usage-row__track">
                                    <span className="usage-row__fill" />
                                  </div>
                                </div>
                              ))
                            ) : (
                              <Badge tone="default">No role history yet</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="No player stats yet" description="Player history appears after approved members start building real attendance records." />
            )}
          </Panel>
        </>
      )}
    </AppShell>
  );
}

export function PlayerStatsPage(): JSX.Element {
  return (
    <ComponentErrorBoundary
      fallback={(error) => (
        <AppShell>
          <DataStatePanel
            title="Player stats hit a render problem"
            description={error.message || 'A runtime error interrupted the player stats view.'}
            actionLabel="Reload"
            onAction={() => window.location.reload()}
          />
        </AppShell>
      )}
      onError={(error) => {
        console.error('PlayerStatsPage render failed.', error);
      }}
    >
      <PlayerStatsWorkspace />
    </ComponentErrorBoundary>
  );
}
